import { tx as translateInterfaceText } from '@/utils/i18n';
import { TimelinePoint } from '../hooks/types/metricsTypes';
import { HistogramDataPoint } from '@/types/pitch';

/**
 * Parse time string (mm:ss or hh:mm:ss) to seconds
 */
export function parseTimeToSeconds(timeStr: string): number {
  const parts = timeStr.split(':').map(Number);
  
  if (parts.length === 2) {
    // mm:ss
    const [mins, secs] = parts;
    return (mins * 60) + secs;
  } else if (parts.length === 3) {
    // hh:mm:ss
    const [hours, mins, secs] = parts;
    return (hours * 3600) + (mins * 60) + secs;
  }
  
  return 0;
}

/**
 * Format seconds to mm:ss or hh:mm:ss
 */
export function formatSecondsToTime(seconds: number, includeHours: boolean = false): string {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (includeHours || hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Calculate retention at specific time using linear interpolation
 */
export function calculateRetentionAtTime(
  targetSeconds: number,
  timeline: TimelinePoint[]
): number {
  if (!timeline || timeline.length === 0) return 0;

  // Se o tempo solicitado for antes do primeiro ponto, retornar o primeiro valor
  if (targetSeconds <= timeline[0].time) {
    const retention = timeline[0].retention;
    return Math.max(0, Math.min(100, Math.round(retention * 10) / 10));
  }

  // Se o tempo solicitado for depois do último ponto, retornar o último valor
  if (targetSeconds >= timeline[timeline.length - 1].time) {
    const retention = timeline[timeline.length - 1].retention;
    return Math.max(0, Math.min(100, Math.round(retention * 10) / 10));
  }

  // Find adjacent points
  let t0 = timeline[0];
  let t1 = timeline[timeline.length - 1];

  // If exact match exists
  const exactMatch = timeline.find(p => p.time === targetSeconds);
  if (exactMatch) {
    const retention = exactMatch.retention;
    return Math.max(0, Math.min(100, Math.round(retention * 10) / 10));
  }

  // Find surrounding points
  for (let i = 0; i < timeline.length - 1; i++) {
    if (timeline[i].time <= targetSeconds && timeline[i + 1].time >= targetSeconds) {
      t0 = timeline[i];
      t1 = timeline[i + 1];
      break;
    }
  }

  // Linear interpolation
  const timeDiff = t1.time - t0.time;
  if (timeDiff === 0) {
    const retention = t0.retention;
    return Math.max(0, Math.min(100, retention));
  }

  const ratio = (targetSeconds - t0.time) / timeDiff;
  const retention = t0.retention + (t1.retention - t0.retention) * ratio;

  // Garantir que o valor esteja entre 0 e 100
  const clampedRetention = Math.max(0, Math.min(100, retention));
  return Math.round(clampedRetention * 10) / 10; // 1 decimal place
}

/**
 * Espectadores no segundo `targetSeconds`: o ponto exato da timeline,
 * interpolação linear entre os vizinhos ou, sem vizinhos, o ponto mais próximo.
 */
export function viewersAtTime(targetSeconds: number, timeline: TimelinePoint[]): number {
  if (!timeline || timeline.length === 0) return 0;

  const exactPoint = timeline.find((point) => point.time === targetSeconds);
  if (exactPoint && exactPoint.viewers_count !== undefined) {
    return exactPoint.viewers_count;
  }

  const beforePoint = timeline.filter((p) => p.time <= targetSeconds).sort((a, b) => b.time - a.time)[0];
  const afterPoint = timeline.filter((p) => p.time >= targetSeconds).sort((a, b) => a.time - b.time)[0];

  if (beforePoint?.viewers_count !== undefined && afterPoint?.viewers_count !== undefined) {
    const timeDiff = afterPoint.time - beforePoint.time;
    if (timeDiff === 0) return beforePoint.viewers_count;
    const ratio = (targetSeconds - beforePoint.time) / timeDiff;
    return Math.round(beforePoint.viewers_count + (afterPoint.viewers_count - beforePoint.viewers_count) * ratio);
  }

  const nearestPoint = timeline
    .filter((p) => p.viewers_count !== undefined)
    .sort((a, b) => Math.abs(a.time - targetSeconds) - Math.abs(b.time - targetSeconds))[0];

  return nearestPoint?.viewers_count || 0;
}

/**
 * Generate histogram data around pitch time (±50s in buckets)
 */
export function generateHistogramData(
  pitchSeconds: number,
  timeline: TimelinePoint[],
  windowSize: number = 50,
  bucketSize: number = 5
): HistogramDataPoint[] {
  if (!timeline || timeline.length === 0) return [];

  // Get max time from timeline (video duration)
  const maxTime = Math.max(...timeline.map(p => p.time));

  const startRange = Math.max(pitchSeconds - windowSize, 0);
  const endRange = Math.min(pitchSeconds + windowSize, maxTime);

  const data: HistogramDataPoint[] = [];
  const addedTimes = new Set<number>();

  // Gerar dados de bucketSize em bucketSize
  for (let t = startRange; t <= endRange; t += bucketSize) {
    const retention = calculateRetentionAtTime(t, timeline);
    // Garantir que retention esteja entre 0 e 100
    const clampedRetention = Math.max(0, Math.min(100, retention));
    data.push({
      time: t,
      retention: clampedRetention,
      isPitch: Math.abs(t - pitchSeconds) <= bucketSize / 2
    });
    addedTimes.add(t);
  }

  // Garantir que o último tempo esteja incluído se não foi adicionado
  if (!addedTimes.has(endRange) && endRange > startRange) {
    const retention = calculateRetentionAtTime(endRange, timeline);
    const clampedRetention = Math.max(0, Math.min(100, retention));
    data.push({
      time: endRange,
      retention: clampedRetention,
      isPitch: Math.abs(endRange - pitchSeconds) <= bucketSize / 2
    });
  }

  // Ordenar por tempo
  return data.sort((a, b) => a.time - b.time);
}

/**
 * Get badge color class based on retention value
 */
export function getRetentionBadgeColor(retention: number): string {
  if (retention >= 50) return 'bg-green-100 text-green-800 border-green-200';
  if (retention >= 30) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  return 'bg-red-100 text-red-800 border-red-200';
}

/**
 * Get retention label based on value
 */
export function getRetentionLabel(retention: number): string {
  if (retention >= 50) return 'Excelente';
  if (retention >= 30) return 'Bom';
  return translateInterfaceText("Atenção");
}

/**
 * Validate time format (mm:ss or hh:mm:ss)
 */
export function validateTimeFormat(timeStr: string): boolean {
  const mmssPattern = /^\d{1,2}:\d{2}$/;
  const hhmmssPattern = /^\d{1,2}:\d{2}:\d{2}$/;
  
  return mmssPattern.test(timeStr) || hhmmssPattern.test(timeStr);
}

/**
 * Validate time against video duration
 */
export function validateTimeAgainstDuration(
  timeStr: string,
  durationInSeconds: number
): { valid: boolean; error?: string } {
  if (!validateTimeFormat(timeStr)) {
    return { 
      valid: false, 
      error: translateInterfaceText("Informe no formato mm:ss ou hh:mm:ss.")
    };
  }
  
  const seconds = parseTimeToSeconds(timeStr);
  
  if (seconds > durationInSeconds) {
    return { 
      valid: false, 
      error: translateInterfaceText("O tempo informado é maior que a duração do vídeo.")
    };
  }
  
  return { valid: true };
}
